<template>
    <div class="page">
        <auth-landing-logo/>

        <div class="page-wrap">
            <img
                src="@/assets/authorization/human-stand.svg"
                class="page-login_image"
            />

            <div class="form-wrap">
                <div class="form-content">
                    <header-main>
                        {{ $t("components.authorization.signin.header") }}
                    </header-main>

                    <form id="loginForm" novalidate @keydown.enter.prevent="login">
                        <div
                            :class="[
                'input-wrap',
                {
                  input_error: v$.form.email.$errors.length || serverErrorMsg,
                },
              ]"
                        >
                            <input-field
                                v-model="v$.form.email.$model"
                                type="email"
                                :placeholder="$t('components.authorization.mail')"
                            />
                        </div>

                        <div
                            class="input-errors"
                            :style="{ height: v$.form.email.$errors.length ? '14px' : 0 }"
                        >
              <span
                  v-for="(error, index) of v$.form.email.$errors"
                  :key="index"
              >
                {{ $t(`components.authorization.${error.$message}`) }}
              </span>
                        </div>

                        <div
                            :class="[
                'input-wrap',
                {
                  input_error:
                    v$.form.password.$errors.length || serverErrorMsg,
                },
              ]"
                        >
                            <input-field
                                v-model="v$.form.password.$model"
                                class="input-password"
                                :type="showPassword ? 'text' : 'password'"
                                :placeholder="$t('components.authorization.password')"
                            />

                            <button
                                class="input-password-icon"
                                @click.prevent="showPassword = !showPassword"
                            >
                                <i
                                    :class="!showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
                                ></i>
                            </button>
                        </div>

                        <div
                            class="input-errors input-errors__server-error"
                            :style="{ height: v$.form.password.$errors.length ? '14px' : 0 }"
                        >
              <span
                  v-for="(error, index) of v$.form.password.$errors"
                  :key="index"
              >
                {{ $t(`components.authorization.${error.$message}`) }}
              </span>

                            <br/>

                            <span v-if="serverErrorMsg">
                {{ $t(`components.authorization.signin.${serverErrorMsg}`) }}
              </span>
                        </div>
                    </form>

                    <button-loading
                        class="btn"
                        type="submit"
                        form="loginForm"
                        :loading="button.loading"
                        :disabled="button.disabled"
                        @click.prevent="login"
                    >
                        {{ $t("components.authorization.signin.button") }}
                    </button-loading>

                    <router-link :to="{ name: 'ForgotPass' }" class="top-link">
                        {{ $t("components.authorization.signin.link") }}
                    </router-link>

                    <router-link :to="{ name: 'Registration' }" class="bottom-link">
                        {{ $t("components.authorization.signin.link1") }}
                    </router-link>

                    <language-select/>

                    <auth-landing-link/>
                </div>

                <img src="@/assets/authorization/wave.svg" class="wave"/>

                <img src="@/assets/authorization/ronm-bg.png" class="ronm-bg"/>

                <img src="@/assets/authorization/cilinder-bf.png" class="cilinder-bf"/>
            </div>
        </div>
    </div>
</template>
<script>
import HeaderMain from "@/components/Recurring/HeaderMain.vue";
import AuthLandingLogo from "@/components/Auth/AuthLandingLogo.vue";
import AuthLandingLink from "@/components/Auth/AuthLandingLink.vue";
import InputField from "@/components/Recurring/InputField.vue";
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";
import LanguageSelect from "@/components/Recurring/LanguageSelect.vue";
import ToastContent from "@/components/Recurring/ToastContent.vue";

import {mapActions, mapGetters} from "vuex";
import {AFTER_LOGIN_PATH} from "@/router";
import {withProps, clearToasts} from "mosha-vue-toastify";

import useVuelidate from "@vuelidate/core";
import {required, email, minLength, helpers} from "@vuelidate/validators";

export default {
    name: "LoginPage",

    components: {
        HeaderMain,
        AuthLandingLogo,
        AuthLandingLink,
        InputField,
        ButtonLoading,
        LanguageSelect,
        ToastContent,
    },

    setup() {
        return {v$: useVuelidate(), clearToasts};
    },

    data() {
        return {
            serverErrorMsg: "",
            showPassword: false,

            form: {
                email: "",
                password: "",
            },

            button: {
                loading: false,
                disabled: false,
            },
        };
    },

    validations() {
        return {
            form: {
                email: {
                    required: helpers.withMessage("required", required),
                    email: helpers.withMessage("email_invalid_error", email),
                },
                password: {
                    required: helpers.withMessage("required", required),
                    min: helpers.withMessage("password_min_length_error", minLength(6)),
                },
            },
        };
    },

    computed: {
        ...mapGetters("auth", ["getLanguage"]),
    },

    methods: {
        ...mapActions("auth", ["LOGIN"]),
        setBtnLoading() {
            if (this.button.loading) {
                this.button.loading = false;
                this.button.disabled = false;

                return;
            }

            this.button.loading = true;
            this.button.disabled = true;
        },

        async login() {
            const isFormCorrect = await this.v$.$validate();

            if (!isFormCorrect) return;

            const payload = {
                email: this.form.email,
                password: this.form.password,
                language: this.getLanguage,
            };

            this.setBtnLoading();

            await this.LOGIN(payload)
                .then((response) => {
                    let hash = response.data.data.hash;

                    dashly.auth(this.form.email, hash);

                    const afterLoginPath = sessionStorage.getItem(AFTER_LOGIN_PATH);
                    sessionStorage.removeItem(AFTER_LOGIN_PATH);

                    this.$router.push(afterLoginPath || {name: "Home"});
                })
                .catch((error) => {
                    if (error.response.status === 401) {
                        this.serverErrorMsg = "auth_error";

                        return;
                    }

                    this.$moshaToast(
                        withProps(ToastContent, {
                            msg: this.$t("components.authorization.signin.toast_error"),
                            type: "error",
                        }),
                        {
                            hideProgressBar: true,
                            timeout: 10000,
                        }
                    );
                })
                .finally(() => this.setBtnLoading());
        },
    },

    unmounted() {
        this.clearToasts();
    },

    mounted() {
        if (this.$route.redirectedFrom?.path.includes("landing_registration")) {
            this.$moshaToast(
                withProps(ToastContent, {
                    msg: this.$t("components.authorization.registration.toast"),
                    type: "success",
                }),
                {
                    hideProgressBar: true,
                }
            );
        }
    },
};
</script>

<style lang="scss" scoped>
@use "@/styles/pages/auth/auth.scss";

#loginForm {
    width: 100%;
    height: 162px;
}

.form-content {
    background: rgba(#ffffff, 0.67) !important;
}
</style>
